<template>
  <div>
    <p class="text-static-default-mid body-2 md:pl-40">
      {{ i18n(translations.tabProducts) }}
    </p>
    <div
      class="hidden grid-cols-2 overflow-hidden px-40 pb-40 pt-16 md:grid lg:grid-cols-5 min-[1536px]:grid-cols-6"
    >
      <ProductCardContent
        v-for="(product, index) in products"
        :key="product.id"
        class="rounded-lg cursor-pointer hover:shadow-long"
        :class="{
          'hidden lg:block': index === 4,
          'hidden min-[1536px]:block': index > 4,
        }"
        :options="{ withStartingFrom: true }"
        :product="mapToProductCardContentProduct(product)"
        @link-click="handleProductClick(product, index)"
      />
    </div>
    <div class="flex flex-col md:hidden">
      <div v-for="(product, index) in products.slice(0, 3)" :key="product.id">
        <RevButtonBase
          class="rounded-sm flex grow items-center gap-8 p-8 text-left hover:bg-static-default-min-hover"
          :to="resolveProductPageLink(product)"
          :underlined="false"
          @click="handleProductClick(product, index)"
        >
          <img alt="" class="rounded-sm h-24" :src="product.image" />
          <div class="flex grow flex-col">
            <span class="body-1-bold">
              {{ product.title }}
            </span>
            <span class="caption">
              {{ i18n(translations.from) }}
              <span class="caption-bold">{{ i18n.price(product.price) }}</span>
              <span
                v-if="product.priceNew"
                class="text-static-info-hi-disabled ml-3 line-through"
              >
                {{ i18n.price(product.priceNew) }}
              </span>
            </span>
          </div>
          <IconChevronRight class="shrink-0" />
        </RevButtonBase>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useRouteLocationWithLocale } from '#imports'

import ProductCardContent from '@backmarket/nuxt-layer-recommendation/ProductCardContent.vue'
import type { Product } from '@backmarket/nuxt-layer-recommendation/models/product'
import { useClickTracking } from '@backmarket/nuxt-layer-recommendation/useProductTracking'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevButtonBase } from '@ds/components/ButtonBase'
import { IconChevronRight } from '@ds/icons/IconChevronRight'

import { PRODUCT as PRODUCT_ROUTES } from '~/scopes/product-core/route-names'

import translations from './ProductSection.translations'

defineProps<{
  products: Product[]
}>()

const emits = defineEmits(['click-product'])

const i18n = useI18n()

const { trackFormSubmit } = useTracking()
const { trackProductClick } = useClickTracking()

const resolveLocalizedRoute = useRouteLocationWithLocale()

function handleProductClick(product: Product, index: number) {
  emits('click-product')
  trackFormSubmit({
    name: product.title,
    payload: { index_used: index, search_type: 'suggested_models' },
    zone: 'header_searchbar',
  })
  trackProductClick(product, {
    list: 'search',
    position: index + 1,
  })
}

function mapToProductCardContentProduct(product: Product) {
  const url = resolveProductPageLink(product)

  return {
    url,
    imageSrc: product.image,
    colorSwatches: product.specifications?.color ?? [],
    title: product.title,
    description: product.description,
    price: product.price,
    discountedPrice: product.discountedPrice,
    priceNew: product.priceNew,
    reviewRating: product.reviewRating,
    listingId: product.id,
    listingPublicId: product.listingPublicId ?? '',
    rawProduct: product,
    deal: product.deal,
    marketingTags: product.marketingTags,
  }
}

function resolveProductPageLink(product: Product) {
  const isMasterPPLink =
    product.productPageLink.name === PRODUCT_ROUTES.MASTER_PP

  return isMasterPPLink
    ? resolveLocalizedRoute({
        name: PRODUCT_ROUTES.MASTER_PP,
        params: {
          slug: product.productPageLink.params.slug,
          id: product.id,
        },
      })
    : resolveLocalizedRoute({
        name: PRODUCT_ROUTES.HOME,
        params: {
          slugV2: product.productPageLink.params.slug,
          uuid: product.id,
        },
      })
}
</script>
